import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {map} from 'rxjs/operators';
import {BehaviorSubject, forkJoin} from 'rxjs';
import {
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    IdentityDocumentStatusesEnum,
    IndividualIdentityDetailsService,
    IndividualIdentityDetailInterface,
    LookupModel,
    IndividualExtendedModel,
    IndividualIdentityDetailModel,
    IndividualIdentityDetailInputModel
} from '@spa/api';
import {RequiredValidatorKey} from '@spa/validators';
import {BaseControl, Helper} from '../../../../shared/classes';
import {IdentityDetailsTrait} from '../../traits';
import {IdentityDetailsFormErrorStatesEnum} from '../../enums'; 
import {
    IdentityDetailIdSelector,
    IdentityDocumentPoISelector,
    IdentityDocumentTypeSelector,
    IdentityDocumentNumberSelector,
    IdentityDocumentStatusSelector,
    IdentityDocumentNumberMaxLength,
    IdentityDetailsFormArraySelector,
    IdentityDocumentExpirationDateSelector,
} from '../../../../../assets/descriptors/panel-clients';

export class IdentityDetailsForm {
    private readonly identityDocumentTypes: LookupModel;
    private readonly identityDetailsService: IndividualIdentityDetailsService;

    public form: FormGroup;
    public individual: IndividualExtendedModel;
    public controls: Array<BaseControl<string | number>[]>;
    public controlsObservable: BehaviorSubject<Array<BaseControl<string | number>[]>>;

    public readonly identityDetailsFormArraySelector: string;

    constructor(
        individual: IndividualExtendedModel,
        identityDocumentTypes: LookupModel,
        identityDetailsService: IndividualIdentityDetailsService
    ) {
        this.controls = [];
        this.individual = individual;
        this.identityDocumentTypes = identityDocumentTypes;
        this.identityDetailsService = identityDetailsService;
        this.identityDetailsFormArraySelector = IdentityDetailsFormArraySelector;

        this.controlsObservable = new BehaviorSubject([]);
    }
    public getRequestModel():IndividualIdentityDetailInputModel{
       return this.getRequestModelArray()[0]; 
    }
    private getRequestModelArray():IndividualIdentityDetailInputModel[]{
        const formArray = this.form.controls[this.identityDetailsFormArraySelector] as FormArray;
       return formArray.controls
            .filter((formGroup: FormGroup) => { 
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === IdentityDetailIdSelector) &&
                        !formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .map((formGroup: FormGroup) => {
                const documentTypeId = formGroup.value[IdentityDocumentTypeSelector] as string;
                const documentNumber = formGroup.value[IdentityDocumentNumberSelector] as string;
                const documentStatus = formGroup.value[IdentityDocumentStatusSelector] as string;
                const expirationDate = formGroup.value[IdentityDocumentExpirationDateSelector] as string;
                const proofOfIdentityId = formGroup.value[IdentityDocumentPoISelector] as number;

                return new IndividualIdentityDetailInputModel(
                    this.individual.id,
                    documentTypeId,
                    documentNumber,
                    !documentStatus ? IdentityDocumentStatusesEnum.Pending : documentStatus,
                    expirationDate,
                    null,
                    proofOfIdentityId
                );   
            }); 
    }

    private createRegisterRequests(formArray: FormArray): ServerResponse<boolean>[] {
        return this.getRequestModelArray().map((model:IndividualIdentityDetailInputModel)=>{
                return this.identityDetailsService.register(model)
                    .pipe(
                        map((response: ServerResponseData<IndividualIdentityDetailInterface>) => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                return Helper.errorsHelper().createErrorResponse<IndividualIdentityDetailInterface>(
                                    response,
                                    IdentityDetailsFormErrorStatesEnum.CreateDetailsFailed
                                );
                            }

                            const data = response.data as IndividualIdentityDetailInterface;
                            const identityDetailModel = new IndividualIdentityDetailModel(data);

                            if (!this.individual.identityDetails) {
                                this.individual.identityDetails = [];
                            }

                            this.individual.identityDetails.push(identityDetailModel);

                            return Helper.createSuccessResponse<boolean>(true) as ApiResponse<boolean>;
                        })
                    );
            });
    }

    private createUpdateRequests(formArray: FormArray): ServerResponse<boolean>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === IdentityDetailIdSelector) &&
                        formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .filter(IdentityDetailsTrait.removeUnchangedIdentityDetails.bind(this, this.individual.identityDetails))
            .map(this.createUpdateRequest.bind(this));
    }

    private createUpdateRequest(formGroup: FormGroup): ServerResponse<boolean> {
        const filteredIdentityDetails = this.individual.identityDetails.filter((detail: IndividualIdentityDetailModel) => {
            return detail.id === formGroup.controls[IdentityDetailIdSelector].value;
        });

        const clonedModel = filteredIdentityDetails[0].serialize();

        filteredIdentityDetails[0].identityDocumentType = formGroup.controls[IdentityDocumentTypeSelector].value;
        filteredIdentityDetails[0].identityNumber = formGroup.controls[IdentityDocumentNumberSelector].value;
        filteredIdentityDetails[0].expirationDate = formGroup.controls[IdentityDocumentExpirationDateSelector].value;
        filteredIdentityDetails[0].reviewStatus = formGroup.controls[IdentityDocumentStatusSelector].value;

        return this.identityDetailsService.update(filteredIdentityDetails[0])
            .pipe(
                map((response: ServerResponseData<null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        const model = new IndividualIdentityDetailModel(clonedModel);

                        this.individual.identityDetails = this.individual.identityDetails
                            .map((detail: IndividualIdentityDetailModel) => {
                                if (detail.id === model.id) {
                                    detail = model;
                                }

                                return detail;
                            });

                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            IdentityDetailsFormErrorStatesEnum.UpdateDetailsFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    initForm(skipStatusControl = false, initForRegistration = false, mappedControls: BaseControl[] = []): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (
            this.individual &&
            this.individual.identityDetails &&
            (this.individual.identityDetails.length !== 0) &&
            !initForRegistration
        ) {
            this.controls = this.individual.identityDetails.map((identityDetail: IndividualIdentityDetailModel) => {
                return IdentityDetailsTrait.initControls(skipStatusControl, identityDetail);
            });
        } else {
            const controls = IdentityDetailsTrait.initControls(skipStatusControl, null, mappedControls);

            this.controls.push(controls);
        }

        const formArray = new FormArray([]);

        this.controls.map((controls: BaseControl[]) => {
            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            formArray.push(formGroup);
        });

        this.form.addControl(this.identityDetailsFormArraySelector, formArray);
        this.controlsObservable.next(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        const emptyFormGroupIndexes = [];
        const formArray = (this.form.controls[this.identityDetailsFormArraySelector] as FormArray);

        const statuses = formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
            const emptyGroupCheck = Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey !== IdentityDetailIdSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value === '');

            if ((emptyGroupCheck.indexOf(false) < 0) && (formGroupIndex > 0)) {
                emptyFormGroupIndexes.push(formGroupIndex);

                return true;
            }

            const validationCheck = Object.keys(formGroup.controls).map((controlKey: string, controlIndex: number) => {
                const formControl = formGroup.controls[controlKey];
                const control = this.controls[formGroupIndex][controlIndex];

                if (formControl.invalid) {
                    const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messageFunction(control.placeholder, IdentityDocumentNumberMaxLength)[errorKey];
                    });

                    control.setErrors(errors);
                }

                if ((control.name === IdentityDocumentExpirationDateSelector) && !control.value && !control.disabled) {
                    control.setError(messageFunction(control.placeholder)[RequiredValidatorKey]);
                }

                return control.errors.length === 0;
            });

            const duplicationCheck = IdentityDetailsTrait.checkForDuplicatedNumbers(
                this.controls,
                formArray,
                formGroup,
                formGroupIndex,
                messageFunction
            );

            return (validationCheck.indexOf(false) < 0) && duplicationCheck;
        });

        if (emptyFormGroupIndexes.length !== 0) {
            IdentityDetailsTrait.removeEmptyGroups(this.controls, emptyFormGroupIndexes, formArray);
        }

        return statuses.indexOf(false) < 0;
    }

    getValidationErrors(): { [key: string]: string[] }[] {
        return this.controls.reduce((reducedArray: { [key: string]: string[] }[], controls: BaseControl[]) => {
            const reducedObject = {};

            controls.map((control: BaseControl) => {
                reducedObject[control.name] = control.errors;
            });

            reducedArray.push(reducedObject);

            return reducedArray;
        }, []);
    }

    setValues(arrayGroupIndex: number, values: { [key: string]: string | number }): void {
        const formArray = this.form.get(this.identityDetailsFormArraySelector) as FormArray;
        const formGroup = formArray.at(arrayGroupIndex);

        if (formGroup) {
            formGroup.patchValue(values);
        }
    }

    createRequest(): ServerResponse<boolean> | null {
        if (this.form.dirty) {
            const formArray = this.form.controls[this.identityDetailsFormArraySelector] as FormArray;

            const requestsStack = this.createRegisterRequests(formArray).concat(...this.createUpdateRequests(formArray));

            if (requestsStack.length !== 0) {
                return forkJoin(requestsStack).pipe(
                    map((responses: ServerResponseData<boolean>[]) => {
                        if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                            return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
            }
        }

        return null;
    }
}
